import type { ScopeNode } from "@/components/ChannelMenu"

// Mock data for the Plan vs Actual wireframe. Figures are made up but shaped
// like VN AOP 2026: six sales channels, products planned in several channels,
// each product in one plan node.

const node = (id: string, name: string, parent_id: string | null, depth: number, is_leaf: boolean): ScopeNode => ({
  id, name, slug: id, parent_id, depth, is_leaf, authorized: true,
})

export const SCOPE_TREE: ScopeNode[] = [
  node("vietnam", "Vietnam", null, 2, false),
  node("marketplace", "Marketplace", "vietnam", 3, false),
  node("tiktok", "Tiktok", "marketplace", 4, true),
  node("shopee", "Shopee", "marketplace", 4, true),
  node("lazada", "Lazada", "marketplace", 4, true),
  node("wholesale", "Wholesale", "vietnam", 3, true),
  node("ecom", "Ecom", "vietnam", 3, true),
  node("retail", "Retail", "vietnam", 3, true),
]

export const MARKET_ID = "vietnam"

// Share of a product's yearly plan each channel carries.
const CHANNEL_SHARE: Record<string, number> = {
  tiktok: 0.27, shopee: 0.28, lazada: 0.02, wholesale: 0.33, ecom: 0.06, retail: 0.04,
}
const CHANNELS = Object.keys(CHANNEL_SHARE)

export const PLAN_NODES = [
  { id: "body-care", path: ["Halio", "Body Care"] },
  { id: "redlight", path: ["Halio", "Redlight"] },
  { id: "oral-care", path: ["Halio", "Oral Care"] },
  { id: "skincare", path: ["Okame", "Skincare"] },
]

// [name, plan node, full-year plan revenue across Vietnam (VND), unit price]
const PRODUCTS: [string, string, number, number][] = [
  ["HSG-B Shower Gel Bergamot", "body-care", 52e9, 380_000],
  ["HRCV2 Body Lotion", "body-care", 31e9, 420_000],
  ["HRI-B Body Scrub", "body-care", 30e9, 350_000],
  ["HRM Redlight Mask", "redlight", 24e9, 2_900_000],
  ["HRT Redlight Therapy Wand", "redlight", 19e9, 3_400_000],
  ["HRW Redlight Wrap", "redlight", 14e9, 2_500_000],
  ["HDT Redlight Device", "redlight", 6e9, 4_200_000],
  ["Toothbrush Sky Blue", "oral-care", 22e9, 650_000],
  ["HSC-C Sonic Brush Heads", "oral-care", 17e9, 240_000],
  ["Water Flosser Mini", "oral-care", 9e9, 990_000],
  ["Okame Cleansing Oil", "skincare", 12e9, 310_000],
  ["Okame Sunscreen SPF50", "skincare", 8e9, 280_000],
]

// Small deterministic pseudo-random number in [0, 1) so the mock stays stable.
function rand(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 233280
  return x - Math.floor(x)
}

export interface ChannelFigures {
  channel: string
  plan: number
  actual: number
  cogs: number
  units: number
}

export interface Product {
  id: string
  name: string
  planNodeId: string
  byChannel: ChannelFigures[]
}

export const PRODUCT_DATA: Product[] = PRODUCTS.map(([name, planNodeId, yearPlan, price], p) => ({
  id: `p${p}`,
  name,
  planNodeId,
  byChannel: CHANNELS
    // A few products are not planned on Lazada or Retail.
    .filter((c, i) => !((c === "lazada" || c === "retail") && rand(p * 7 + i) < 0.35))
    .map((channel, i) => {
      const plan = yearPlan * CHANNEL_SHARE[channel]
      // Jan 1 – Oct 2 is ~75% of the year; actual lands between 45% and 105% of the year plan.
      const actual = plan * (0.45 + rand(p * 13 + i * 3) * 0.6)
      const gm = 0.35 + rand(p * 17 + i * 5) * 0.3
      return { channel, plan, actual, cogs: actual * (1 - gm), units: Math.round(actual / price) }
    }),
}))

// Top-down year target per plan node per channel: deliberately not equal to the
// bottom-up plan, as on the real page.
export function yearTarget(planNodeId: string, channel: string): number {
  const nodePlan = PRODUCT_DATA.filter((p) => p.planNodeId === planNodeId)
    .flatMap((p) => p.byChannel)
    .filter((c) => c.channel === channel)
    .reduce((sum, c) => sum + c.plan, 0)
  const idx = PLAN_NODES.findIndex((n) => n.id === planNodeId)
  return nodePlan * (1.05 + rand(idx * 31 + CHANNELS.indexOf(channel)) * 0.35)
}

// Leaf sales channels under a scope node (the node itself when it is a leaf).
export function leafChannelsUnder(scopeId: string): string[] {
  const out: string[] = []
  const walk = (id: string) => {
    const n = SCOPE_TREE.find((s) => s.id === id)
    if (!n) return
    if (n.is_leaf) out.push(n.id)
    SCOPE_TREE.filter((s) => s.parent_id === id).forEach((c) => walk(c.id))
  }
  walk(scopeId)
  return out
}
